<template>
  <div
    class="d-flex flex-column justify-content-center align-items-center h-100"
  >
    <div class="w-50 p-5 reg-form-container">
      <div class="m-3">
        <div class="reg-form-title">
          {{
            $t(
              '_web_tournament_settings_registration-form',
              'Registration form'
            )
          }}
        </div>
        <div class="reg-form-sub-title">
          {{
            $t(
              '_web_form_tournament_registration_provide_info',
              'Please provide all additional information for registration on the tournament'
            )
          }}
        </div>
      </div>
      <div class="m-3 row">
        <RegForm v-model="fields" item-classes="col-6 mt-3" />
        <div class="col-12 pt-4">
          <div class="d-flex flex-column align-items-center">
            <div v-if="rulesLink" class="mb-5">
              <FormCheckbox
                v-model="isRulesAgreed"
                class="flex-column font-size-xs"
                name="agreement"
                :error="errors.first('agreement')"
                :val="true"
              >
                <TextWithLinks
                  content="I agree to {:rules} using my data by the tournament organisation."
                  :content-props="{
                    rules: {
                      text: 'Rules',
                      href: rulesLink,
                    },
                  }"
                  translate-key="_web_tournament-rules-agree"
                />
              </FormCheckbox>
            </div>
            <div class="my-3">
              <SubmitBtn
                id="submitTournamentJoin"
                :label="$t('_web_tournament-join-submit', 'Submit')"
                :loading="isLoading"
                :disabled="!isRulesAgreed[0] && !!rulesLink"
                @click.native="formSubmit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RegForm from '@/views/tournaments/player/tournament-registration/components/RegistrationForm';
import FormCheckbox from '@/components/form/HeraFormCheckbox';
import TextWithLinks from '@/components/text/TextWithLinks';
import SubmitBtn from '@/components/form/HeraFormSubmitBtn';
import registrationFormMixin from '@/mixins/registrationForm';
import joinAction from '@/views/tournaments/mixins/joinTournamentAction';
import { FORM_FIELDS_RELATIONS } from '@/constants/form';
import { emptyObject } from '@/utils/factories';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import { PROFILE, CLAN } from '@/constants/accounts';

export default {
  name: 'TournamentRegistrationMainForm',
  components: {
    RegForm,
    FormCheckbox,
    TextWithLinks,
    SubmitBtn,
  },
  inject: ['$validator'],
  mixins: [registrationFormMixin, joinAction],
  data() {
    return {
      fields: emptyObject(),
      isRulesAgreed: [],
    };
  },
  computed: {
    ...mapState('my', ['account', 'accountId']),
    ...mapGetters([
      'registrationTournament',
      'registrationFields',
      'registrationData',
      'currentTournament',
      'currentTournamentStages',
    ]),
    tournamentID() {
      return this.$lodash.get(this, '$route.params.tournamentID', '');
    },
    teamID() {
      return this.$lodash.get(this, '$route.params.teamId', '');
    },
    rulesLink() {
      // todo don't load rulesLink by first page
      return this.$lodash.get(this, 'registrationTournament.rules_link', '');
    },
  },
  created() {
    this.saveFields();
  },
  methods: {
    ...mapMutations(['setRegistrationData']),
    ...mapActions(['fetchTournamentCustomFields', 'errorNotify']),
    saveFields() {
      const { custom_clan_fields, custom_fields } = this.registrationFields;
      const { player_type: playerType } = this.registrationTournament;
      const isClanReg = playerType === CLAN;
      const fields = isClanReg ? custom_clan_fields : custom_fields;

      this.fields = this.fillFieldsByRelations({
        data: this.account,
        fields,
        relations: FORM_FIELDS_RELATIONS[playerType],
      });
      const noExtraFields = !this.fields.length;
      if (noExtraFields) {
        this.formSubmit();
      }
    },
    formSubmit() {
      this.$validator
        .validateAll()
        .then(isValid => {
          if (!isValid) {
            return Promise.reject(
              this.$t('_web_form-validation-failed', 'Form validation failed')
            );
          }

          const { player_type: playerType } = this.registrationTournament;
          this.afterJoin = () =>
            this.$router.push(
              'create-roster'
              // currentTournament.squad_size > 1
              //   ?
              //   : `/tournament/${currentTournament.id}`
            );
          if (playerType === CLAN) {
            this.wrapClanRegForm(this.fields);
            this.$router.push({
              name: 'tournamentRegistrationCreateRoster',
              params: {
                clanID: this.teamID,
              },
              query: {
                ...this.$route.query,
              },
            });
          } else if (playerType === PROFILE) {
            this.wrapProfileRegForm(this.fields);
          }
        })
        .catch(this.errorNotify)
        .finally(
          window.dataLayer.push({
            event: 'tournament join register form',
            userID: this.account._id,
            from: this.$route.path,
            tournamentId: this.currentTournament.id,
            gameName: this.currentTournament.game,
            squadSize: this.currentTournament.squad_size,
            tournamentName: this.currentTournament.title,
            tournamentFormat: this.currentTournamentStages[0]
              .block_configuration.type,
            tournamentCountry: this.currentTournament.country,
            tournamentPool: this.currentTournament.prize.prize_pool,
            regForm: this.currentTournament.requires_registration_form,
            tournamentPrivate:
              this.currentTournament.registration_type === 'closed'
                ? 'true'
                : 'false',
            fromSource: this.$route.query.source
              ? this.$route.query.source
              : '',
            tournamentOrganizer: this.currentTournament.event_organizer_name,
          })
        );
    },
    wrapClanRegForm(fields) {
      // const { clan_id } = this.registrationTournament;

      if (!this.teamID) {
        throw this.$t(
          '_web_tournament-registration-error-wrong-clan-id',
          'Unknown error with clan account, please start registration again'
        );
      }

      const fieldsObj = this.reduceFieldsData(fields, { clan_id: this.teamID });
      this.setRegistrationData({
        ...this.registrationData,
        ...{
          clan: fieldsObj,
        },
        clanId: this.teamID,
      });
    },
    wrapProfileRegForm(fields) {
      if (!this.accountId) {
        throw this.$t(
          '_web_tournament-registration-error-wrong-user-id',
          'Unknown error with user account, please reload page and try again'
        );
      }

      const fieldsObj = this.reduceFieldsData(fields, {
        user_id: this.accountId,
      });
      this.setRegistrationData({
        ...this.registrationData,
        ...{
          players: [fieldsObj],
        },
      });
      this.regularJoinProcedure(this.tournamentID, 'profile', this.teamID);
    },
  },
};
</script>
<style lang="scss">
.tournamentRegistrationForm {
  .reg-form-container {
    border: solid 1px var(--ph-input-borders);
    border-radius: 20px;
    min-width: 450px;

    @media (max-width: 600px) {
      min-width: 90vw;
      width: 100% !important;
    }
    .reg-form-title {
      font-family: 'Exo';
      font-weight: 700;
      font-size: 1.5rem;
      line-height: 2rem;
    }
    .reg-form-sub-title {
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.75rem;
    }
  }
}

@include media-breakpoint-down(lg) {
  .tournament-join-form-wrapper {
    max-width: 50%;
  }
}

#submitTournamentJoin {
  background-color: #ffffff;
  border-radius: 5px;
  color: black;
}
</style>
